package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.CreateInvitationRequest
import com.elan.edubridge.shared.domain.model.InvitationResult
import com.elan.edubridge.shared.domain.model.InviteeType
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.StudentData
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlin.js.Date
import kotlin.js.Promise
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement

/**
 * 초대 컬렉션은 클라이언트 read가 금지되어 있어 발급 결과를 다시 조회할 수 없다. 따라서 이 모달은
 * 성공 후에도 닫힐 때까지 [InvitationResult.code]를 DOM에 유지하고, 즉시 복사할 수 있게 한다.
 */
object InvitationIssueModal {
    private val scope = MainScope()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun openForStudent(session: SessionState, academyId: String, student: StudentData) {
        if (session.requiresGuardianConsent || !session.activePermission.atLeast(Permission.EDITOR)) {
            Toast.error("학생·보호자 초대는 편집자 이상 권한이 필요해요.")
            return
        }
        open(academyId = academyId, student = student)
    }

    fun openForTeacher(session: SessionState, academyId: String) {
        if (session.requiresGuardianConsent || session.activePermission != Permission.ADMIN) {
            Toast.error("선생님 초대는 관리자 권한이 필요해요.")
            return
        }
        open(academyId = academyId, student = null)
    }

    private fun open(academyId: String, student: StudentData?) {
        val teacherInvitation = student == null
        val title = if (teacherInvitation) "선생님 초대 발급" else "${student.name.escapeHtml()} 학생 초대"
        val targetDescription = if (teacherInvitation) {
            "선생님 초대 코드를 발급해요. 수락한 계정이 이 학원의 선생님으로 연결돼요."
        } else {
            "${student.name.escapeHtml()} 학생 또는 보호자에게 전달할 초대 코드를 발급해요."
        }
        val inviteeTypeField = if (teacherInvitation) {
            """
            <p class="eb-meta">선생님 초대에는 학생을 연결하지 않아요.</p>
            """.trimIndent()
        } else {
            """
            <div class="eb-field">
                <label for="invitation-invitee-type">초대 대상</label>
                <select id="invitation-invitee-type">
                    <option value="STUDENT">학생</option>
                    <option value="GUARDIAN">보호자</option>
                </select>
            </div>
            <div class="eb-field" id="invitation-relation-field" hidden>
                <label for="invitation-relation">보호자 관계</label>
                <input id="invitation-relation" maxlength="20" placeholder="예: 어머니">
                <p class="eb-meta">선택 입력 · 비워 두면 ‘보호자’로 표시돼요.</p>
            </div>
            """.trimIndent()
        }

        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <div class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="invitation-modal-title">
                <div class="eb-modal__header">
                    <h2 id="invitation-modal-title">$title</h2>
                    <button class="eb-icon-button" id="invitation-close" aria-label="닫기">${icon("icon-close", "md")}</button>
                </div>
                <div class="eb-modal__body">
                    <p>$targetDescription</p>
                    <form id="invitation-form">
                        $inviteeTypeField
                        <div class="eb-field">
                            <label for="invitation-expiry">유효 기간</label>
                            <select id="invitation-expiry">
                                <option value="24">24시간</option>
                                <option value="72" selected>72시간</option>
                                <option value="168">7일</option>
                                <option value="336">14일</option>
                            </select>
                        </div>
                        <p class="eb-form-error" role="alert" id="invitation-error" hidden></p>
                    </form>
                    <section id="invitation-result" aria-live="polite" hidden></section>
                </div>
                <div class="eb-modal__footer">
                    <button class="eb-button eb-button--ghost" id="invitation-cancel" type="button">취소</button>
                    <button class="eb-button eb-button--primary" id="invitation-submit" type="submit" form="invitation-form">초대 코드 발급</button>
                </div>
            </div>
        """.trimIndent()
        document.body!!.appendChild(backdrop)

        val closeButton = backdrop.querySelector("#invitation-close") as? HTMLButtonElement
        val cancelButton = backdrop.querySelector("#invitation-cancel") as? HTMLButtonElement
        val submitButton = backdrop.querySelector("#invitation-submit") as? HTMLButtonElement
        var isIssuing = false
        fun setIssuing(issuing: Boolean) {
            isIssuing = issuing
            closeButton?.disabled = issuing
            cancelButton?.disabled = issuing
            submitButton?.disabled = issuing
        }
        fun close() {
            if (!isIssuing) backdrop.remove()
        }
        closeButton?.onclick = { close(); null }
        cancelButton?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }

        val inviteeTypeSelect = backdrop.querySelector("#invitation-invitee-type") as? HTMLSelectElement
        val relationField = backdrop.querySelector("#invitation-relation-field") as? HTMLElement
        fun updateInviteeFields() {
            val isGuardian = inviteeTypeSelect?.value == InviteeType.GUARDIAN.name
            if (isGuardian) relationField?.removeAttribute("hidden") else relationField?.setAttribute("hidden", "")
        }
        inviteeTypeSelect?.onchange = { updateInviteeFields(); null }
        updateInviteeFields()

        val form = backdrop.querySelector("#invitation-form") as? HTMLFormElement ?: return
        form.onsubmit = { event ->
            event.preventDefault()
            if (!isIssuing) {
                val inviteeType = if (teacherInvitation) {
                    InviteeType.TEACHER
                } else {
                    InviteeType.entries.firstOrNull { it.name == inviteeTypeSelect?.value } ?: InviteeType.STUDENT
                }
                val expiresInHours = (backdrop.querySelector("#invitation-expiry") as? HTMLSelectElement)
                    ?.value?.toIntOrNull()
                if (expiresInHours == null || expiresInHours !in 1..336) {
                    showError(backdrop, "유효 기간을 다시 선택해 주세요.")
                } else {
                    val relation = if (inviteeType == InviteeType.GUARDIAN) {
                        (backdrop.querySelector("#invitation-relation") as? HTMLInputElement)?.value?.trim()?.ifEmpty { null }
                    } else {
                        null
                    }
                    setIssuing(true)
                    scope.launch {
                        try {
                            val result = commandRepository.createInvitation(
                                CreateInvitationRequest(
                                    academyId = academyId,
                                    inviteeType = inviteeType,
                                    targetId = if (inviteeType == InviteeType.TEACHER) null else student?.id,
                                    relation = relation,
                                    expiresInHours = expiresInHours,
                                ),
                            )
                            if (result.isSuccess && result.code != null) {
                                showResult(backdrop, result)
                            } else {
                                showError(backdrop, issueErrorMessage(result))
                            }
                        } catch (_: Throwable) {
                            showError(backdrop, "초대 코드 발급에 실패했어요. 다시 시도해 주세요.")
                        } finally {
                            setIssuing(false)
                        }
                    }
                }
            }
            null
        }
    }

    private fun showResult(backdrop: HTMLElement, result: InvitationResult) {
        val code = result.code ?: return
        val invitationLink = invitationLink(code)
        (backdrop.querySelector("#invitation-form") as? HTMLElement)?.setAttribute("hidden", "")
        (backdrop.querySelector("#invitation-submit") as? HTMLElement)?.setAttribute("hidden", "")
        (backdrop.querySelector("#invitation-cancel") as? HTMLElement)?.textContent = "닫기"
        val expiry = result.expiresAt?.let { "${formatLocalExpiry(it)} 만료" } ?: "만료 시각 정보를 확인할 수 없어요."
        val resultElement = backdrop.querySelector("#invitation-result") as? HTMLElement ?: return
        resultElement.innerHTML = """
            <section class="eb-card" aria-label="발급된 초대 코드">
                <h3>초대 코드가 발급되었어요</h3>
                <p class="eb-meta">$expiry</p>
                <p class="eb-meta">초대 링크</p>
                <p><code>${invitationLink.escapeHtml()}</code></p>
                <button class="eb-button eb-button--primary eb-button--sm" id="invitation-copy-link" type="button">초대 링크 복사</button>
                <p class="eb-meta">QR 코드</p>
                <div class="eb-qr-code" id="invitation-qr" role="img" aria-label="초대 링크 QR 코드"></div>
                <p class="eb-meta">전화로 전달할 초대 코드</p>
                <p><strong>${code.escapeHtml()}</strong></p>
                <button class="eb-button eb-button--ghost eb-button--sm" id="invitation-copy-code" type="button">코드 복사</button>
                <p class="eb-meta">초대 코드는 이 화면을 닫으면 다시 볼 수 없어요. 지금 안전하게 전달해 주세요.</p>
                <p class="eb-form-error" role="alert" id="invitation-copy-error" hidden></p>
            </section>
        """.trimIndent()
        resultElement.removeAttribute("hidden")
        (resultElement.querySelector("#invitation-copy-link") as? HTMLButtonElement)?.onclick = {
            copyText(
                resultElement,
                invitationLink,
                "초대 링크를 복사했어요.",
                "초대 링크를 자동으로 복사하지 못했어요. 위 링크를 직접 복사해 주세요.",
            )
            null
        }
        (resultElement.querySelector("#invitation-copy-code") as? HTMLButtonElement)?.onclick = {
            copyText(
                resultElement,
                code,
                "초대 코드를 복사했어요.",
                "코드를 자동으로 복사하지 못했어요. 위 코드를 직접 복사해 주세요.",
            )
            null
        }
        val qrContainer = resultElement.querySelector("#invitation-qr") as? HTMLElement
        if (qrContainer != null) {
            scope.launch {
                if (document.body?.contains(qrContainer) == true) {
                    QrRenderer.renderInto(qrContainer, invitationLink, pixelSize = 200)
                }
            }
        }
    }

    private fun invitationLink(code: String): String =
        "https://elan-edubridge.web.app/invite#c=$code"

    private fun copyText(resultElement: HTMLElement, value: String, successMessage: String, failureMessage: String) {
        try {
            val clipboard = window.navigator.asDynamic().clipboard
            if (clipboard == null || jsTypeOf(clipboard.writeText) != "function") {
                showCopyError(resultElement, failureMessage)
                return
            }
            val promise = clipboard.writeText(value) as? Promise<dynamic>
            if (promise == null) {
                showCopyError(resultElement, failureMessage)
                return
            }
            promise.then(
                { _: dynamic -> Toast.success(successMessage); Unit },
                { _: dynamic -> showCopyError(resultElement, failureMessage); Unit },
            )
        } catch (_: Throwable) {
            showCopyError(resultElement, failureMessage)
        }
    }

    private fun showError(backdrop: HTMLElement, message: String) {
        val errorElement = backdrop.querySelector("#invitation-error") as? HTMLElement ?: return
        errorElement.textContent = message
        errorElement.removeAttribute("hidden")
    }

    private fun showCopyError(resultElement: HTMLElement, message: String) {
        val errorElement = resultElement.querySelector("#invitation-copy-error") as? HTMLElement ?: return
        errorElement.textContent = message
        errorElement.removeAttribute("hidden")
    }

    private fun issueErrorMessage(result: InvitationResult): String {
        val serverMessage = result.errorMessage
        return when {
            serverMessage == "이미 계정이 연결된 학생입니다." -> serverMessage
            serverMessage != null -> serverMessage
            else -> "초대 코드 발급에 실패했어요. 다시 시도해 주세요."
        }
    }

    /** ISO UTC 값을 브라우저 로컬 시각으로 보여준다. */
    private fun formatLocalExpiry(iso: String): String {
        val date = Date(iso)
        val hours = date.getHours()
        val period = if (hours >= 12) "오후" else "오전"
        val hour12 = if (hours % 12 == 0) 12 else hours % 12
        return "${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일 $period $hour12:${date.getMinutes().toString().padStart(2, '0')}"
    }
}
